/**
 * InstancedOht.tsx
 *
 * High-performance GPU Instanced Overhead Hoist Transport (OHT) fleet rendering.
 * Implements official Three.js dynamic instancing (webgl_instancing_dynamic)
 * for large AMHS vehicle fleets (50 to 500+ OHTs):
 *
 * ONE OHT MODEL -> GPU instance batches -> OHT #1, OHT #2 ... OHT #500
 *
 * Reconstructs the complete high-fidelity industrial OHT vehicle:
 * 1. Ultra-glossy cleanroom white chassis body & rounded pods
 * 2. Dark anodized mounting blocks & chassis spine
 * 3. CNC machined satin stainless steel bogie fork brackets
 * 4. Precision machined grooved steel roller wheels & axle pins
 * 5. Top robotic handling flange & hardware fittings
 * 6. Cyan/Green cleanroom status beacon LEDs
 *
 * Reduces draw calls for 500 OHT vehicles from ~25,000 draw calls down to
 * exactly 6 GPU draw calls total.
 */

import React, { useMemo } from 'react';
import { getAmhsKitTemplateParts } from './amhsKitTemplateStore';
import { useAmhsKitTemplateRevision } from './useAmhsKitTemplateRevision';
import * as THREE from 'three';
import {
  instancedAssetManager,
  InstancedMultiMeshGroup,
  type InstanceTransform,
  type SubMeshPart,
} from './InstancedAssetManager';
import {
  getIndustrialDarkChassisMaterial,
  getIndustrialWhiteMaterial,
  getBrushedAluminumMaterial,
  getPolishedChromeMaterial,
  getStatusLedMaterial,
} from '../../utils/threeResourcePool';

export const OHT_TEMPLATE_SCALE = 1.0;
export const OHT_TEMPLATE_DIMS = { w: 10.0, h: 7.5, d: 5.5 };

/**
 * Builds the complete high-fidelity 3D OHT vehicle template asset.
 */
export function buildOhtTemplateModel(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'OhtVehicleTemplate';

  const S = 10.0; // Reference scale factor
  const darkChassis = getIndustrialDarkChassisMaterial();
  const whiteBody = getIndustrialWhiteMaterial();
  const satinBracket = getBrushedAluminumMaterial();
  const polishedSteel = getPolishedChromeMaterial();
  const cyanLed = getStatusLedMaterial('#06B6D4', 2.0);
  const greenLed = getStatusLedMaterial('#10B981', 1.8);

  // 1. Central Overhead Structural Spine & Top Gripper Flange
  const spine = new THREE.Mesh(
    new THREE.BoxGeometry(0.72 * S, 0.14 * S, 0.36 * S),
    darkChassis
  );
  spine.position.set(0, 0.18 * S, 0);
  spine.name = 'OhtSpine';
  root.add(spine);

  const topFlange = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18 * S, 0.22 * S, 0.08 * S, 24),
    polishedSteel
  );
  topFlange.position.set(0, 0.28 * S, 0);
  topFlange.name = 'OhtTopFlange';
  root.add(topFlange);

  // 2. Main Aerodynamic Cleanroom White Casing / Pods
  const mainCasing = new THREE.Mesh(
    new THREE.BoxGeometry(0.85 * S, 0.34 * S, 0.44 * S),
    whiteBody
  );
  mainCasing.position.set(0, 0, 0);
  mainCasing.name = 'OhtMainCasing';
  root.add(mainCasing);

  // Lower Left & Right Guide Pods ("Legs")
  const podL = new THREE.Mesh(
    new THREE.BoxGeometry(0.82 * S, 0.28 * S, 0.09 * S),
    whiteBody
  );
  podL.position.set(0, -0.24 * S, 0.20 * S);
  podL.name = 'OhtPodL';
  root.add(podL);

  const podR = new THREE.Mesh(
    new THREE.BoxGeometry(0.82 * S, 0.28 * S, 0.09 * S),
    whiteBody
  );
  podR.position.set(0, -0.24 * S, -0.20 * S);
  podR.name = 'OhtPodR';
  root.add(podR);

  // 3. Wheel Bogie Units (Front & Rear Pairs)
  const bogiePositions: [number, number, number][] = [
    [-0.34 * S, 0.22 * S, 0.16 * S],
    [0.34 * S, 0.22 * S, 0.16 * S],
    [-0.34 * S, 0.22 * S, -0.16 * S],
    [0.34 * S, 0.22 * S, -0.16 * S],
  ];

  bogiePositions.forEach(([bx, by, bz], i) => {
    // Mounting Block
    const block = new THREE.Mesh(
      new THREE.BoxGeometry(0.12 * S, 0.10 * S, 0.08 * S),
      darkChassis
    );
    block.position.set(bx, by, bz);
    block.name = `OhtBogieBlock_${i}`;
    root.add(block);

    // Satin Fork Bracket
    const bracket = new THREE.Mesh(
      new THREE.BoxGeometry(0.06 * S, 0.14 * S, 0.06 * S),
      satinBracket
    );
    bracket.position.set(bx, by + 0.04 * S, bz);
    bracket.name = `OhtBogieBracket_${i}`;
    root.add(bracket);

    // Steel Roller Wheel
    const wheel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07 * S, 0.07 * S, 0.04 * S, 18),
      polishedSteel
    );
    wheel.rotation.x = Math.PI / 2;
    wheel.position.set(bx, by + 0.08 * S, bz);
    wheel.name = `OhtBogieWheel_${i}`;
    root.add(wheel);
  });

  // 4. Status Beacon Light Bars (Front Cyan / Rear Green)
  const frontLed = new THREE.Mesh(
    new THREE.BoxGeometry(0.04 * S, 0.06 * S, 0.24 * S),
    cyanLed
  );
  frontLed.position.set(0.44 * S, -0.02 * S, 0);
  frontLed.name = 'OhtStatusLedFront';
  root.add(frontLed);

  const rearLed = new THREE.Mesh(
    new THREE.BoxGeometry(0.04 * S, 0.06 * S, 0.24 * S),
    greenLed
  );
  rearLed.position.set(-0.44 * S, -0.02 * S, 0);
  rearLed.name = 'OhtStatusLedRear';
  root.add(rearLed);

  return root;
}

function getOrRegisterOhtParts(): SubMeshPart[] {
  return getAmhsKitTemplateParts('OhtVehicle', () => {
    let parts = instancedAssetManager.getAssetParts('OhtVehicle');
    if (!parts) {
      const model = buildOhtTemplateModel();
      parts = instancedAssetManager.registerAsset('OhtVehicle', model);
    }
    return parts;
  });
}

export type OhtFleetVehicleSpec = {
  id: string;
  position: [number, number, number];
  rotationY?: number; // radians
  scale?: number;
  color?: string;
  visible?: boolean;
};

export type InstancedOhtProps = {
  vehicles: readonly OhtFleetVehicleSpec[];
  onSelect?: (id: string) => void;
};

export const InstancedOht: React.FC<InstancedOhtProps> = React.memo(
  ({ vehicles, onSelect }) => {
    const kitRevision = useAmhsKitTemplateRevision();
    const parts = useMemo(() => getOrRegisterOhtParts(), [kitRevision]);

    const instances = useMemo<InstanceTransform[]>(() => {
      const out: InstanceTransform[] = [];
      for (let i = 0; i < vehicles.length; i++) {
        const v = vehicles[i]!;
        const s = v.scale ?? 1.0;
        out.push({
          id: v.id,
          position: v.position,
          rotation: [0, v.rotationY ?? 0, 0],
          scale: [s, s, s],
          visible: v.visible !== false,
          color: v.color,
        });
      }
      return out;
    }, [vehicles]);

    if (vehicles.length === 0) return null;

    return (
      <group name="InstancedOhtFleet">
        <InstancedMultiMeshGroup
          layerId="oht-fleet"
          parts={parts}
          instances={instances}
          onSelect={onSelect}
          castShadow
          receiveShadow
        />
      </group>
    );
  }
);

InstancedOht.displayName = 'InstancedOht';
export default InstancedOht;
